/**
 * Serviço de persistência de filtros usando SessionStorage
 *
 * Os filtros são mantidos durante a sessão do navegador (incluindo F5),
 * mas são limpos ao fechar a aba, fazer logout ou trocar de estabelecimento.
 */

const STORAGE_PREFIX = "zig-filters";

export interface ExtractFilters {
	dateRange: {
		since: Date;
		until: Date;
	};
	groupBy?: string | null;
	filter?: string;
	searchQuery?: string;
	typeIds?: string[];
}

export interface StorageHistoryFilters {
	selectedStorage?: string;
	selectedCategories: string[];
	selectedProducts: string[];
	filterMode: "product" | "category";
	hideProductsWithProductRule: boolean;
	selectedDateFilterBy: "interval" | "events";
	dateInterval: {
		since: Date;
		until: Date;
	};
}

type FilterType = "extract" | "storage-history";

/**
 * Gera a chave para armazenamento baseada no tipo de filtro e placeId
 */
function getStorageKey(type: FilterType, placeId: string): string {
	return `${STORAGE_PREFIX}-${type}-${placeId}`;
}

/**
 * Salva filtros no SessionStorage
 */
export function saveFilters<T>(type: FilterType, placeId: string, filters: T): void {
	try {
		const key = getStorageKey(type, placeId);
		// Serializa as datas corretamente
		const serialized = JSON.stringify(filters, (_key, value) => {
			if (value instanceof Date) {
				return { __type: "Date", value: value.toISOString() };
			}
			return value;
		});
		sessionStorage.setItem(key, serialized);
	} catch (error) {
		console.error("Erro ao salvar filtros no SessionStorage:", error);
	}
}

/**
 * Recupera filtros do SessionStorage
 */
export function loadFilters<T>(type: FilterType, placeId: string): T | null {
	try {
		const key = getStorageKey(type, placeId);
		const stored = sessionStorage.getItem(key);

		if (!stored) return null;

		// Deserializa as datas corretamente
		const parsed = JSON.parse(stored, (_key, value) => {
			if (value && typeof value === "object" && value.__type === "Date") {
				return new Date(value.value);
			}
			return value;
		});

		return parsed as T;
	} catch (error) {
		console.error("Erro ao carregar filtros do SessionStorage:", error);
		return null;
	}
}

/**
 * Remove filtros específicos do SessionStorage
 */
export function clearFilters(type: FilterType, placeId: string): void {
	try {
		const key = getStorageKey(type, placeId);
		sessionStorage.removeItem(key);
	} catch (error) {
		console.error("Erro ao limpar filtros do SessionStorage:", error);
	}
}

/**
 * Remove TODOS os filtros de um estabelecimento
 */
export function clearAllFiltersForPlace(placeId: string): void {
	try {
		const types: FilterType[] = ["extract", "storage-history"];
		types.forEach(type => clearFilters(type, placeId));
	} catch (error) {
		console.error("Erro ao limpar todos os filtros:", error);
	}
}

/**
 * Remove TODOS os filtros de TODOS os estabelecimentos
 * Usado no logout
 */
export function clearAllFilters(): void {
	try {
		const keys = Object.keys(sessionStorage);
		keys.forEach(key => {
			if (key.startsWith(STORAGE_PREFIX)) {
				sessionStorage.removeItem(key);
			}
		});
	} catch (error) {
		console.error("Erro ao limpar todos os filtros:", error);
	}
}
